import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDialog } from "../../../providers";
import { LoginDialog, RegisterDialog, useAuth } from "../../auth";
import { useUserExperience } from "../../user-experiences";
import { AttendExperienceDialog } from "../dialogs";
import { useExperiencesForEventSeries } from "../hooks";

const LoadingState = () => (
  <Grid container sx={{ minHeight: "100vh" }}>
    <Grid item xs={12} sm={4} lg={3} sx={{ display: "flex", flexDirection: "column", padding: 2 }}>
      <Skeleton variant="rounded" animation="wave" sx={{ flex: 1 }} />
      <Card sx={{ my: 2, p: 2 }}>
        <CardHeader sx={{ p: 1, mb: 2 }} title={<Skeleton variant="text" />} subheader={<Skeleton variant="text" />} />
        <Skeleton variant="rectangular" height={30} />
        <Skeleton variant="rectangular" height={20} width={100} sx={{ mt: 1, mx: "auto" }} />
      </Card>
    </Grid>
    <Grid item xs={12} sm={8} lg={9}>
      <Card sx={{ filter: "opacity(0.8)", m: 1, mt: 2, p: 0 }}>
        <Box p={2}>
          <Typography variant="overline">What's Happening?</Typography>
          <Typography variant="body2">
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width={"60%"} />
          </Typography>
        </Box>
        <Box p={2}>
          <Typography variant="overline">When's it Going Down?</Typography>
          <Typography variant="body2">
            <Skeleton variant="text" />
            <Skeleton variant="text" width={"60%"} />
          </Typography>
        </Box>
        <Box p={2}>
          <Typography variant="overline">Where is it?</Typography>
          <Typography variant="body1">
            <Skeleton variant="text" />
          </Typography>
          <Typography variant="body2">
            <Skeleton variant="text" />
            <Skeleton variant="text" width={"60%"} />
          </Typography>
        </Box>
        <Box p={2}>
          <Typography variant="overline">Boring Legal Stuff</Typography>
          <Typography variant="body2">
            <Skeleton variant="text" />
            <Skeleton variant="text" width={"60%"} />
          </Typography>
        </Box>
      </Card>
    </Grid>
  </Grid>
);

export const PublicEventSeriesPage = () => {
  const { seriesId } = useParams();
  const [experience, setExperience] = useState<any>(null);

  const { open: openDialog } = useDialog();
  const { isAuthenticated } = useAuth();
  const { isAttending, attendExperience } = useUserExperience(experience?.id);

  const { upcomingExperiences, loading } = useExperiencesForEventSeries(Number.parseInt(seriesId ?? ""));

  useEffect(() => {
    if (!upcomingExperiences?.length) return;

    setExperience(upcomingExperiences[0]);
  }, [upcomingExperiences]);

  const dateStr = useMemo(() => {
    if (!experience) return "";

    const date = DateTime.fromISO(experience.eventBeginDate, {
      zone: "utc",
    }).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
    return `${date} @ ${experience.startTime}`;
  }, [experience]);

  const handleRegister = () => {
    openDialog({
      component: RegisterDialog,
      props: { experienceId: experience.id },
    }).then((res) => {
      if (res.wasCancelled) return;

      openDialog({
        component: AttendExperienceDialog,
        props: { experience },
        showFloatingCloseButton: true,
      });
    });
  };

  const handleLogin = () => openDialog({ component: LoginDialog });

  const handleAttend = () => {
    attendExperience().then((res) => {
      if (res.wasCancelled) return;

      openDialog({
        component: AttendExperienceDialog,
        props: { experience },
        showFloatingCloseButton: true,
      });
    });
  };

  if (loading || !experience) {
    return <LoadingState />;
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        position: "relative",
        minHeight: "100vh",
        paddingBottom: { xs: 30, sm: 0 },
        // TODO: Considering moving this to a style file
        "&::before": {
          content: '""',
          display: "block",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `url("${experience.imageUrl}")`,
          backgroundSize: "cover",
          backgroundPosition: "top",
          backgroundOrigin: "content-box",
          filter: "opacity(0.1) blur(2px)",
          animation: {
            xs: "bgScrollX 30s linear infinite",
            sm: "bgScrollY 60s linear infinite",
          },
        },
        "@keyframes bgScrollY": {
          "0%": { backgroundPosition: "top" },
          "50%": { backgroundPosition: "bottom" },
          "100%": { backgroundPosition: "top" },
        },
        "@keyframes bgScrollX": {
          "0%": { backgroundPosition: "left" },
          "50%": { backgroundPosition: "right" },
          "100%": { backgroundPosition: "bottom" },
        },
      }}
    >
      <Grid
        item
        xs={12}
        sm={4}
        lg={3}
        sx={{
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            m: 1,
            flex: 1,
            display: "flex",
          }}
        >
          <img alt={experience.headline} style={{ width: "100%", flex: 1, margin: -4 }} src={experience.imageUrl} />
        </Box>
        <Card
          elevation={5}
          sx={{
            mx: 1,
            my: 2,
            p: 2,
            ["@media (width <= 600px)"]: {
              position: "fixed",
              bottom: 6,
              left: 6,
              right: 6,
            },
          }}
        >
          <CardHeader
            title={experience.headline}
            subheader={dateStr}
            subheaderTypographyProps={{ fontSize: 14 }}
            sx={{ p: 1, mb: 2 }}
          />
          {!isAuthenticated && !isAttending && (
            <>
              <Button fullWidth variant="contained" onClick={handleRegister}>
                Register to Attend
              </Button>
              <Button fullWidth variant="text" size="small" sx={{ mt: 1 }} onClick={handleLogin}>
                Login
              </Button>
            </>
          )}
          {isAuthenticated && !isAttending && (
            <Button fullWidth variant="contained" onClick={handleAttend}>
              Attend Experience
            </Button>
          )}
          {isAttending && <Alert severity="success">You are attending this experience</Alert>}
        </Card>
      </Grid>
      <Grid item xs={12} sm={8} lg={9}>
        <Card sx={{ filter: "opacity(0.9)", m: 1, p: 0 }}>
          <Box p={2}>
            <Typography variant="overline">What's Happening?</Typography>
            <Typography variant="body2">{experience.description}</Typography>
          </Box>
          <Box p={2}>
            <Typography variant="overline">When's it Going Down?</Typography>
            <Typography variant="body2">{dateStr}</Typography>
          </Box>
          <Box p={2}>
            <Typography variant="overline">Where is it?</Typography>
            <Typography variant="body1">{experience.locationName}</Typography>
            <Typography variant="body2">{experience.locationAddress}</Typography>
          </Box>
          <Box p={2}>
            <Typography variant="overline">Boring Legal Stuff</Typography>
            <Typography variant="body2">{experience.disclaimer}</Typography>
          </Box>
        </Card>

        {/** TODO: Consider moving this to a seperate file */}
        <Card sx={{ my: 2, mx: 1, filter: "opacity(0.9)" }}>
          <Typography variant="overline" sx={{ m: 2, display: "block" }}>
            Other Events In This Series
          </Typography>
          <List>
            {upcomingExperiences
              .filter((exp) => exp.id !== experience.id)
              .map((exp) => (
                <ListItem
                  key={exp.id}
                  secondaryAction={
                    <Button variant="outlined" onClick={() => setExperience(exp)}>
                      View
                    </Button>
                  }
                >
                  <ListItemText
                    primary={DateTime.fromISO(exp.eventBeginDate, {
                      zone: "utc",
                    }).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                    secondary={`@ ${exp.startTime}`}
                  />
                </ListItem>
              ))}
          </List>
        </Card>
      </Grid>
    </Grid>
  );
};
